import {BOOTSTRAP} from "../../../common/resolutionConstants";
import type {Resolution} from "../../../common/resolution";
import type {Maybe} from "../../../bootstrap/common/types";

const MAX_TILES = 30;

const ROW_SIZE_XS = 1;
const ROW_SIZE_SM = 2;
const ROW_SIZE_MD_TO_XL = 3;
const ROW_SIZE_XXL_PLUS = 4;

const ROW_SIZE_NARROW_XL = 4;
const ROW_SIZE_NARROW_XXL_PLUS = 6;

// Grid - List on Mobile
const AMOUNTS_MULTI_ROW_LIST_ON_MOBILE: AmountsPerRow = {
    small: {xs: 4, sm: 4, mdToLg: ROW_SIZE_MD_TO_XL, xl: ROW_SIZE_MD_TO_XL, xxlPlus: ROW_SIZE_XXL_PLUS},
    medium: {xs: 6, sm: 6, mdToLg: 2 * ROW_SIZE_MD_TO_XL, xl: 2 * ROW_SIZE_MD_TO_XL, xxlPlus: 2 * ROW_SIZE_XXL_PLUS},
    large: {xs: 8, sm: 8, mdToLg: 3 * ROW_SIZE_MD_TO_XL, xl: 3 * ROW_SIZE_MD_TO_XL, xxlPlus: 3 * ROW_SIZE_XXL_PLUS},
    max: {xs: MAX_TILES, sm: MAX_TILES, mdToLg: MAX_TILES, xl: MAX_TILES, xxlPlus: MAX_TILES}
};

// Grid - Always Tiles
const AMOUNTS_MULTI_ROW_ALWAYS_TILES: AmountsPerRow = {
    small: {xs: 3 * ROW_SIZE_XS, sm: 2 * ROW_SIZE_SM, mdToLg: ROW_SIZE_MD_TO_XL, xl: ROW_SIZE_MD_TO_XL, xxlPlus: ROW_SIZE_XXL_PLUS},
    medium: {xs: 4 * ROW_SIZE_XS, sm: 3 * ROW_SIZE_SM, mdToLg: 2 * ROW_SIZE_MD_TO_XL, xl: 2 * ROW_SIZE_MD_TO_XL, xxlPlus: 2 * ROW_SIZE_XXL_PLUS},
    large: {xs: 6 * ROW_SIZE_XS, sm: 4 * ROW_SIZE_SM, mdToLg: 3 * ROW_SIZE_MD_TO_XL, xl: 3 * ROW_SIZE_MD_TO_XL, xxlPlus: 3 * ROW_SIZE_XXL_PLUS},
    max: {xs: MAX_TILES, sm: MAX_TILES, mdToLg: MAX_TILES, xl: MAX_TILES, xxlPlus: MAX_TILES}
};

// Grid narrow tiles
const AMOUNTS_MULTI_ROW_ALWAYS_TILES_NARROW: AmountsPerRow = {
    small: {xs: 3 * ROW_SIZE_XS, sm: 2 * ROW_SIZE_SM, mdToLg: ROW_SIZE_MD_TO_XL, xl: ROW_SIZE_NARROW_XL, xxlPlus: ROW_SIZE_NARROW_XXL_PLUS},
    medium: {xs: 4 * ROW_SIZE_XS, sm: 3 * ROW_SIZE_SM, mdToLg: 2 * ROW_SIZE_MD_TO_XL, xl: 2 * ROW_SIZE_NARROW_XL, xxlPlus: 2 * ROW_SIZE_NARROW_XXL_PLUS},
    large: {xs: 6 * ROW_SIZE_XS, sm: 4 * ROW_SIZE_SM, mdToLg: 3 * ROW_SIZE_MD_TO_XL, xl: 3 * ROW_SIZE_NARROW_XL, xxlPlus: 3 * ROW_SIZE_NARROW_XXL_PLUS},
    max: {xs: MAX_TILES, sm: MAX_TILES, mdToLg: MAX_TILES, xl: MAX_TILES, xxlPlus: MAX_TILES}
};

// Slider
const AMOUNTS_SINGLE_ROW: AmountsPerRow = {
    small: {xs: 3, sm: 4, mdToLg: 5, xl: 5, xxlPlus: 6},
    medium: {xs: 5, sm: 6, mdToLg: 8, xl: 8, xxlPlus: 10},
    large: {xs: 7, sm: 8, mdToLg: 12, xl: 12, xxlPlus: 15},
    max: {xs: MAX_TILES, sm: MAX_TILES, mdToLg: MAX_TILES, xl: MAX_TILES, xxlPlus: MAX_TILES}
};

// Slider narrow
const AMOUNTS_SINGLE_ROW_NARROW: AmountsPerRow = {
    small: {xs: 3, sm: 4, mdToLg: 5, xl: 6, xxlPlus: 8},
    medium: {xs: 5, sm: 6, mdToLg: 8, xl: 10, xxlPlus: 12},
    large: {xs: 7, sm: 8, mdToLg: 12, xl: 15, xxlPlus: 18},
    max: {xs: MAX_TILES, sm: MAX_TILES, mdToLg: MAX_TILES, xl: MAX_TILES, xxlPlus: MAX_TILES}
};

type AmountsPerRow = {
    [size in SizeName]: AmountsPerBreakpoint;
};

type AmountsPerBreakpoint = {
    [breakpoint in BreakpointKey]: number;
};

type BreakpointKey = "xs" | "sm" | "mdToLg" | "xl" | "xxlPlus"
type SizeName = "small" | "medium" | "large" | "max";

class Size {
    public static readonly SMALL = new Size("small");
    public static readonly MEDIUM = new Size("medium");
    public static readonly LARGE = new Size("large");
    public static readonly MAX = new Size("max");

    private constructor(public readonly name: SizeName) {
    }

    public static all(): Size[] {
        return [Size.SMALL, Size.MEDIUM, Size.LARGE, Size.MAX];
    }

    public static parseWithDefault(name: Maybe<string>, defaultValue: Size): Size {
        return this.all()
                .findFirst(size => size.name === name)
            ?? defaultValue;
    }
}

enum Arrangement {
    MOBILE_LIST = "mobile-condensed",
    TILES_ONLY = "tiles-only"
}

function parseArrangement(rawArrangement: Maybe<string>): Arrangement {
    return rawArrangement === Arrangement.MOBILE_LIST
        ? Arrangement.MOBILE_LIST
        : Arrangement.TILES_ONLY;
}

class BreakpointRange {
    public static readonly PHONE = new BreakpointRange("xs");
    public static readonly TABLET_PORTRAIT = new BreakpointRange("sm");
    public static readonly UP_TO_DESKTOP = new BreakpointRange("mdToLg");
    public static readonly DESKTOP_LARGE = new BreakpointRange("xl");
    public static readonly FROM_DESKTOP_ULTRA_WIDE = new BreakpointRange("xxlPlus");

    private constructor(public readonly name: BreakpointKey) {
    }

    public static getCurrent(resolution: Resolution): BreakpointRange {
        if (resolution.upTo(BOOTSTRAP.XS)) {
            return BreakpointRange.PHONE;
        }
        if (resolution.upTo(BOOTSTRAP.SM)) {
            return BreakpointRange.TABLET_PORTRAIT;
        }
        if (resolution.upTo(BOOTSTRAP.LG)) {
            return BreakpointRange.UP_TO_DESKTOP;
        }
        if (resolution.upTo(BOOTSTRAP.XL)) {
            return BreakpointRange.DESKTOP_LARGE;
        }
        return BreakpointRange.FROM_DESKTOP_ULTRA_WIDE;
    }
}

abstract class BaseFeedPreviewSize {
    protected readonly amounts: AmountsPerBreakpoint;
    protected readonly arrangement: Arrangement;

    public constructor(rawSize: Maybe<string>, protected resolution: Resolution, rawArrangement?: Maybe<string>) {
        const size = Size.parseWithDefault(rawSize, Size.SMALL);
        this.arrangement = parseArrangement(rawArrangement);
        this.amounts = this.amountsSource()[size.name];
    }

    protected abstract amountsSource(): AmountsPerRow;

    public getTileAmount(): number {
        return this.amounts[BreakpointRange.getCurrent(this.resolution).name];
    }
}

export class MultiRowFeedPreviewSize extends BaseFeedPreviewSize {

    public constructor(rawSize: Maybe<string>, resolution: Resolution, rawArrangement?: Maybe<string>) {
        super(rawSize, resolution, rawArrangement);
    }

    protected amountsSource(): AmountsPerRow {
        return this.arrangement === Arrangement.MOBILE_LIST
            ? AMOUNTS_MULTI_ROW_LIST_ON_MOBILE
            : AMOUNTS_MULTI_ROW_ALWAYS_TILES;
    }
}

export class MultiRowFeedPreviewNarrowSize extends BaseFeedPreviewSize {
    protected amountsSource(): AmountsPerRow {
        return AMOUNTS_MULTI_ROW_ALWAYS_TILES_NARROW;
    }
}

export class SingleRowFeedPreviewSize extends BaseFeedPreviewSize {
    protected amountsSource(): AmountsPerRow {
        return AMOUNTS_SINGLE_ROW;
    }
}

export class SingleRowFeedPreviewNarrowSize extends BaseFeedPreviewSize {
    protected amountsSource(): AmountsPerRow {
        return AMOUNTS_SINGLE_ROW_NARROW;
    }
}